import { useCallback, useState } from 'react';

/**
 * Tracks a chart panel's Submit-confirmed selection alongside a version
 * counter that increments on every submit, independent of whether the
 * submitted value is reference/value-equal to the previous one.
 *
 * Why the counter: `ChartDataSectionShell` re-announces its polite live
 * region only when its `summary` prop changes, and React bails out of a
 * state update (and thus a re-render) entirely when `setState` is called
 * with a value that's `Object.is`-equal to the current one. Re-submitting
 * the same pick (or first submitting a pick whose summary text happens to
 * match the pre-submit default) would otherwise produce a silent Submit
 * click — no re-render, no re-announcement. Passing this counter as the
 * shell's `announcementKey` forces a fresh announcement on every submit
 * regardless of whether the derived summary text actually changed.
 */
export function useSubmittedSelection<T>(): [T | null, number, (value: T) => void] {
    const [value, setValue] = useState<T | null>(null);
    const [announcementKey, setAnnouncementKey] = useState(0);

    const submit = useCallback((next: T) => {
        setValue(next);
        setAnnouncementKey((key) => key + 1);
    }, []);

    return [value, announcementKey, submit];
}
